/**
 * Version polling for automatic silent refresh on deploy.
 *
 * Polls /version.json every 5 minutes. When a new version is detected,
 * sets a flag and stops polling. The router integration checks this flag
 * before each navigation — if set, does a full page load instead of
 * client-side transition, seamlessly loading the new version.
 *
 * Pattern used by Linear, Notion, GitHub.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY_MS = 30_000; // 30 seconds after load

const CURRENT_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';

let newVersionAvailable = false;
let pollIntervalId: ReturnType<typeof setInterval> | null = null;

async function checkVersion(): Promise<void> {
  if (!CURRENT_VERSION) return; // dev mode — no version file

  try {
    const res = await fetch('/version.json', { cache: 'no-cache' });
    if (!res.ok) return;
    const data = (await res.json()) as { version: string };
    if (data.version && data.version !== CURRENT_VERSION) {
      newVersionAvailable = true;
      // Stop polling — we already know there's a new version
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
    }
  } catch {
    // Network error — skip this check
  }
}

/**
 * Start polling for version changes. Call once at app startup.
 */
export function startVersionPolling(): void {
  if (!CURRENT_VERSION) return; // skip in dev

  // First check after 30s (let the app settle)
  setTimeout(checkVersion, INITIAL_DELAY_MS);

  // Then every 5 minutes
  pollIntervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
}

/**
 * Returns true if a newer version has been deployed since this page loaded.
 * Used by the router to decide whether to do a hard navigation.
 */
export function isNewVersionAvailable(): boolean {
  return newVersionAvailable;
}
